import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { IMessagesTypeEnum } from "@ryde/controls";
import { useYupValidationResolver } from "@ryde/redux";
import {
  errors,
  getAccessToken,
  Log,
  RecaptchaEnterpriseActions,
  setAccessToken,
} from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  createMessage,
  isLoggedInLandingAtom,
  IVerifyCodeLandingModel,
  LANDING_VALID_OTP_REGEX,
  landingMessagesAtom,
  LandingStepEnum,
  LoginStepFormFieldsEnum,
  useLandingActions,
  useLandingApiAuth,
  useLandingOtpErrors,
  useLandingRecaptchaToken,
  VerifyCodeStepFormFieldsEnum,
} from "@ryde/utils-landings";

import { yupVerifyCodeLandingValidationSchema } from "./parts/verify-code.form.validation";

export const useStepVerifyCode = () => {
  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const [searchParams] = useSearchParams();
  const setIsLoggedIn = useSetRecoilState(isLoggedInLandingAtom);
  const { goToStep } = useLandingActions();
  const { loginOtp, requestOtp } = useLandingApiAuth();
  const { handleErrors } = useLandingOtpErrors();
  const { getRecaptchaToken } = useLandingRecaptchaToken();
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { t } = useTranslation();

  const resolver = useYupValidationResolver(
    yupVerifyCodeLandingValidationSchema,
    "landing",
  );

  const methods = useForm<IVerifyCodeLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      verificationCode: "",
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const phoneParams = searchParams.get(LoginStepFormFieldsEnum.Phone);
  const verificationCodeWatchValue = watch(
    VerifyCodeStepFormFieldsEnum.VerificationCode,
  );

  const isContinueCanBeApply = useMemo(() => {
    const isValidOtp = LANDING_VALID_OTP_REGEX.test(verificationCodeWatchValue);
    return isValid && isValidOtp && phoneParams;
  }, [isValid, phoneParams, verificationCodeWatchValue]);

  const handleCodeVerification = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    const verificationCode = getValues(
      VerifyCodeStepFormFieldsEnum.VerificationCode,
    );
    const phone = searchParams.get(LoginStepFormFieldsEnum.Phone);
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.LOGIN,
    );

    if (verificationCode && phone) {
      setApiLoading(true);

      try {
        setServerErrors(createMessage([]));

        const { success, error, data } =
          (await loginOtp({ verificationCode, phone, recaptchaToken })) ?? {};

        if (success && !error && data?.access_token) {
          Log.debug("Auth token received for phone: ", phone);
          setAccessToken(data?.access_token);

          // Uncomment if you want to handle refresh token
          // if (data?.refresh_token) {
          //   setRefreshToken(data?.refresh_token);
          // }

          if (getAccessToken()) {
            setIsLoggedIn(true);
            goToStep(LandingStepEnum.ChooseStationStep);
            setServerErrors(createMessage([]));
          }
        } else if (error) {
          handleErrors({
            error,
            defaultAction(e) {
              setServerErrors(createMessage([t("errors.errorOtpIncorrect")]));
            },
          });
        }
      } catch (error) {
        Log.warn("Error while verifying code", error);
        setServerErrors(createMessage([t("errors.authenticationError")]));
      } finally {
        setApiLoading(false);
      }
    }
  });

  const resendOtp = useEvent(async (): Promise<void> => {
    const workerId = searchParams.get(LoginStepFormFieldsEnum.WorkerId);
    const phone = searchParams.get(LoginStepFormFieldsEnum.Phone);
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.REQUEST_OTP,
    );

    if (workerId && phone) {
      setApiLoading(true);

      try {
        const requestOtpResponse = await requestOtp({
          recaptchaToken,
          workerId,
          phone,
        });

        const { success, error } = requestOtpResponse ?? {};

        if (success) {
          setServerErrors(
            createMessage(
              [t("landing.resendOtpSuccess")],
              IMessagesTypeEnum.SUCCESS,
            ),
          );
          clearErrors(VerifyCodeStepFormFieldsEnum.VerificationCode);
        } else {
          handleErrors({
            error,
            defaultAction(e) {
              setServerErrors(
                createMessage([errors.auth.errorWhileRequestOTP]),
              );
            },
          });
        }
      } catch (error) {
        Log.warn("Error while requesting OTP", error);
        setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
      } finally {
        setApiLoading(false);
      }
    }
  });

  return { methods, handleCodeVerification, resendOtp, isContinueCanBeApply };
};
